import type { FC } from 'react'
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'

import { WizardStep, Wizard, MessagePopup } from '@skytech/ui'
import { Container } from '@skytech/ui/Container'
import { getFormattedPhoneNumber, isOnlyLetters } from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useIsAdmin } from '@/hooks/access'
import { useEverSelectedCategories } from '@/modules/ecom-policy/hooks'
import queries from '@/modules/ecom-policy/queries'
import { ecomPolicyResources } from '@/modules/ecom-policy/resources'
import employeeResources from '@/resources/employees'
import { commonQueries } from '@/resources/queries'
import { APP_ROUTES } from '@/util/app-routes'

import { getCostObjectCustomerId, getGroupedDataList } from './helpers'
import { ShopNewOrderContextProvider, useShopNewOrderContext } from './ShopNewOrder.context'
import StepProductsAccessories from './steps/products/step-accessories/StepProductAccessories'
import StepProducts from './steps/products/step-products/StepProducts'
import StepConfirm from './steps/step-confirm/StepConfirm'
import StepPolicy from './steps/step-policy/StepPolicy'
import StepUser from './steps/step-user/StepUser'
import type { NewOrderStepName } from './types'

const ShopNewOrder: FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setIsWizardMode } = useAppContext()
  const isAdmin = useIsAdmin()

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const prevActiveStepName = useRef<NewOrderStepName | null>(null)

  const shopCtx = useShopNewOrderContext()
  const [comment] = shopCtx.comment
  const [costObjectId] = shopCtx.costObjectId
  const [selectedPolicyId] = shopCtx.selectedPolicyId
  const [selectedAddressId] = shopCtx.selectedAddressId
  const [selectedProductIds] = shopCtx.selectedProductIds
  const [mandatoryProducts, setMandatoryProducts] = shopCtx.mandatoryProducts
  const [transportationProduct] = shopCtx.transportationProduct
  const [customAddress] = shopCtx.customAddress
  const [selectedCategoryIds] = shopCtx.selectedCategoryIds
  const [stockFilter] = shopCtx.stockFilter
  const [activeStepName] = shopCtx.activeStepName
  const [isWholeVendorsCatalogueVisible] = shopCtx.isWholeVendorsCatalogueVisible
  const [productQuantityById, setProductQuantityById] = shopCtx.productQuantityById
  const [contactName] = shopCtx.contactName
  const [contactNumber] = shopCtx.contactNumber
  const [isCostObjectValid] = shopCtx.isCostObjectValid
  const [isPrivateDeliveryAddress] = shopCtx.isPrivateDeliveryAddress
  const [orderUserType] = shopCtx.orderUserType

  const [everSelectedCategoryIds, setEverSelectedCategoryIds] =
    useEverSelectedCategories(selectedCategoryIds)

  const policyListQuery = useQuery(
    costObjectId ? queries.costObjectPolicyList(costObjectId) : [''],
    () => {
      if (!costObjectId) throw Error('Cost object id missing!')
      return ecomPolicyResources.getCostObjectPolicyList(costObjectId)
    },
    { enabled: Boolean(costObjectId) }
  )
  const selectedPolicy = policyListQuery.data?.find(({ id }) => id === selectedPolicyId)
  const isRelationsIgnored = Boolean(selectedPolicy?.isRelationsIgnored)

  const { data: addresses } = useQuery(
    queries.shopAddressesSetup(),
    () => {
      if (!selectedPolicy?.vendorId) throw Error('Policy data is missing!')
      return ecomPolicyResources.getShopAddressSetup(selectedPolicy.vendorId, costObjectId)
    },
    {
      enabled:
        Boolean(selectedPolicyId) &&
        Boolean(costObjectId) &&
        Boolean(selectedPolicy) &&
        Boolean(selectedPolicy?.vendorId),
    }
  )

  useEffect(() => {
    // Reset everSelectedCategoryIds on step change
    if (prevActiveStepName.current !== activeStepName) {
      setEverSelectedCategoryIds(null)
    }

    prevActiveStepName.current = activeStepName
  }, [activeStepName, setEverSelectedCategoryIds])

  const { data } = useQuery(commonQueries.employees(), () => employeeResources.getList(isAdmin))

  const employee = data?.employees?.find(employee => employee.id === costObjectId)
  const customerId = costObjectId ? employee?.customerId : undefined

  function getPriceListQuery() {
    if (!selectedPolicyId) return ['']
    const costObjectCustomerId = getCostObjectCustomerId({
      costObjectId,
      orderUserType,
      data,
    })
    if (isWholeVendorsCatalogueVisible)
      return queries.policyProductsFromWholeVendorCatalogue(selectedPolicyId, costObjectCustomerId)
    return queries.policyPriceList(selectedPolicyId, costObjectCustomerId)
  }

  const priceListQuery = useQuery(
    getPriceListQuery(),
    () => {
      if (!selectedPolicyId) throw Error('No policy selected!')
      const costObjectCustomerId = getCostObjectCustomerId({
        costObjectId,
        orderUserType,
        data,
      })
      if (isWholeVendorsCatalogueVisible)
        return ecomPolicyResources.getPolicyPriceListForWholeVendorCatalogue(
          selectedPolicyId,
          costObjectCustomerId
        )
      return ecomPolicyResources.getPolicyPriceList(selectedPolicyId, costObjectCustomerId)
    },
    { enabled: Boolean(selectedPolicyId) }
  )

  const list = useMemo(
    () =>
      getGroupedDataList({
        priceList: priceListQuery.data?.response,
        groupNames: priceListQuery.data?.groupNames,
        stockFilter,
        selectedCategoryIds,
        selectedProductIds,
        activeStepName,
        everSelectedCategoryIds,
        isRelationsIgnored,
      }),
    [
      activeStepName,
      everSelectedCategoryIds,
      isRelationsIgnored,
      priceListQuery.data?.groupNames,
      priceListQuery.data?.response,
      selectedCategoryIds,
      selectedProductIds,
      stockFilter,
    ]
  )

  const { groupedAndFilteredAccessories, groupedAndFiltered, selectableAccessoryIds } = list

  // Until the list is loaded, this is unknown
  const isOnlyAccessoriesPolicy =
    !priceListQuery.isLoading &&
    Object.values(groupedAndFiltered).filter(group => !group.isMandatory).length === 0
  const isAccessoriesAvailable =
    Object.values(groupedAndFilteredAccessories).filter(group => !group.isMandatory).length > 0
  const isAccessoriesVisible = isAccessoriesAvailable || activeStepName === 'ACCESSORIES'
  const getOrderData = () => {
    const mandatoryProductIds = mandatoryProducts?.map(({ id }) => id)
    const transportationProductId = transportationProduct?.id
    const selectedProducts = [...(selectedProductIds || []), ...(mandatoryProductIds || [])]?.map(
      id => ({
        id,
        quantity: productQuantityById[id],
        costObjectId,
      })
    )
    if (transportationProductId) {
      selectedProducts.push({ id: transportationProductId, quantity: 1, costObjectId })
    }
    const selectedAddress = addresses?.find(({ id }) => selectedAddressId === id)
    const { address, zip, city } = selectedAddress || customAddress || {}
    let contactPhoneNumber = null
    if (employee?.isLocation) {
      contactPhoneNumber = contactNumber?.phoneNumber
      if (contactNumber?.phoneCountryCode && contactNumber?.phoneNumber) {
        const formattedNumber = getFormattedPhoneNumber({
          value: contactNumber?.phoneNumber,
          countryId: contactNumber?.phoneCountryCode,
        })
        contactPhoneNumber = formattedNumber
      }
    }

    return {
      selectedProducts,
      ecomPolicyId: selectedPolicyId,
      customerId,
      addressId: selectedAddressId,
      address,
      zip,
      city,
      comment,
      contactName: employee?.isLocation ? contactName : null,
      contactPhoneCountryCode: employee?.isLocation ? contactNumber.phoneCountryCode : null,
      contactPhoneNumber,
      deliveryAddressIsPrivate: isPrivateDeliveryAddress,
    }
  }

  /**
   * Products marked mandatory should not be visible in product selection.
   * When user reaches the final step mandatory products should be automatically added.
   * Accessory products that are not selectable due to product parent restrictions should not be added even though mandatory.
   */
  const selectMandatoryProducts = useCallback(() => {
    const data = priceListQuery.data
    if (!data) return
    const mandatoryProducts = data.response.filter(({ id, isMandatory, isAccessory, catId }) => {
      // Transportation product
      if (catId === 15) return false
      if (!isAccessory || isRelationsIgnored) return isMandatory
      return isMandatory && selectableAccessoryIds.includes(id)
    })

    setMandatoryProducts(mandatoryProducts)
    const mandatoryProductQuantityById = mandatoryProducts.reduce<Record<string, number>>(
      (acc, product) => {
        acc[product.id] = 1
        return acc
      },
      {}
    )
    setProductQuantityById({ ...productQuantityById, ...mandatoryProductQuantityById })
  }, [
    priceListQuery.data,
    setMandatoryProducts,
    setProductQuantityById,
    productQuantityById,
    isRelationsIgnored,
    selectableAccessoryIds,
  ])

  const { mutate: postShopOrder, isLoading: isPosting } = useMutation(
    ecomPolicyResources.postShopOrder,
    {
      onSuccess({ orderId }) {
        navigate(
          APP_ROUTES.HARDWARE_ECOM_POLICY_ORDER.replace(':ecomPolicyOrderId', String(orderId))
        )
        queryClient.invalidateQueries(queries.policyOrderList(), { refetchType: 'all' })
      },
      onError() {
        setIsErrorModalOpen(true)
      },
    }
  )

  const orderProducts = priceListQuery.data?.response.filter(product =>
    selectedProductIds?.includes(product.id)
  )
  if (mandatoryProducts) orderProducts?.push(...mandatoryProducts)
  if (transportationProduct) orderProducts?.push(transportationProduct)
  const orderSummary = orderProducts?.reduce(
    (acc, cur) => {
      const quantity = productQuantityById[cur.id]

      return {
        cost: acc.cost + quantity * cur.price,
        products: acc.products + quantity,
      }
    },
    {
      cost: 0,
      products: 0,
    }
  )

  const productCurrency = Object.values(list.groupedAndFiltered)[0]?.currency
  const orderSummaryInfo = orderSummary ? { ...orderSummary, currency: productCurrency } : undefined

  const isCustomAddressValid =
    customAddress?.address &&
    customAddress.city &&
    isOnlyLetters(customAddress.city) &&
    customAddress.zip
  const isAddressValid = Boolean(
    // -1 is the address id we send to indicate it's a new, custom address
    selectedAddressId === -1 ? isCustomAddressValid : selectedAddressId
  )
  const isContactInfoRequires = employee?.isLocation
  const isContactInfoValid = isContactInfoRequires
    ? Boolean(contactNumber.phoneNumber && contactName)
    : true
  const isMaxAmountExceeded = (orderSummary?.cost || 0) > (selectedPolicy?.maximumAmount || 0)
  const isAnyProductsAvailableForSelectedPolicy =
    Object.values(groupedAndFiltered).length || Object.values(groupedAndFilteredAccessories).length

  const onNewOrderComplete = () => {
    const order = getOrderData()
    postShopOrder(order)
  }

  return (
    <Container className="shop">
      <Container.Header>
        <Translation id="Shop" />
      </Container.Header>

      <Wizard
        skipButtonTextDefault={<Translation id="Skip" />}
        nextButtonTextDefault={<Translation id="Next Step" />}
        previousButtonTextDefault={<Translation id="Previous Step" />}
        isLoading={isPosting || priceListQuery.isInitialLoading}
        onIsWizardModeSet={setIsWizardMode}
        onComplete={onNewOrderComplete}
      >
        <WizardStep
          displayNavigation={Boolean(costObjectId)}
          previousButtonText={costObjectId ? <Translation id="Back" /> : ''}
          nextButtonText={costObjectId ? <Translation id="Next step" /> : ' '}
          isValid={Boolean(costObjectId) && isCostObjectValid}
          name={<Translation id="User" />}
        >
          <StepUser employee={employee} />
        </WizardStep>

        <WizardStep
          name={<Translation id="Policy" />}
          isValid={Boolean(selectedPolicyId && isAnyProductsAvailableForSelectedPolicy)}
        >
          <StepPolicy customerId={customerId} employee={employee} />
        </WizardStep>

        <WizardStep
          name={<Translation id="Products" />}
          displayNavigation={false}
          isHidden={isOnlyAccessoriesPolicy}
          isValid={isAccessoriesAvailable ? true : Boolean(selectedProductIds?.length)}
        >
          <StepProducts
            isAccessoriesAvailable={isAccessoriesAvailable}
            orderSummary={orderSummaryInfo}
          />
        </WizardStep>

        <WizardStep
          displayNavigation={false}
          name={<Translation id="Product accessories" />}
          isValid={Boolean(selectedProductIds?.length)}
          isHidden={!isAccessoriesVisible}
        >
          <StepProductsAccessories orderSummary={orderSummaryInfo} />
        </WizardStep>

        <WizardStep
          nextButtonText={<Translation id="Confirm Order" />}
          name={<Translation id="Address/ confirm" />}
          isValid={
            Boolean(selectedProductIds?.length) &&
            isAddressValid &&
            !isMaxAmountExceeded &&
            isContactInfoValid
          }
        >
          <StepConfirm
            isMaxAmountExceeded={isMaxAmountExceeded}
            cost={orderSummary?.cost || 0}
            employee={employee}
            selectMandatoryProducts={selectMandatoryProducts}
          />
        </WizardStep>
      </Wizard>
      <MessagePopup
        isOpen={isErrorModalOpen}
        title={<Translation id="Create order" />}
        message={
          <Translation id="We encountered a problem while creating your order. Please refresh and try again." />
        }
        onClose={() => setIsErrorModalOpen(false)}
      />
    </Container>
  )
}

export default () => (
  <ShopNewOrderContextProvider>
    <ShopNewOrder />
  </ShopNewOrderContextProvider>
)
